import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CartPageContext } from "@insite/content-library/Pages/CartPage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useLaunchDarklyFlags from "@MasterBluePrint/Common/Hooks/useLaunchDarklyFlags";
import React from "react";
import { css } from "styled-components";

export interface CartPageContainerStyles {
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    cartLinesContainer?: InjectableCss;
    fulfillmentMethodContainer?: InjectableCss;
    cartTotalContainer?: InjectableCss;
    productCarouselContainer?: InjectableCss;
}

export const cartPageContainerStyles: CartPageContainerStyles = {
    container: {
        css: css`
            /* stylelint-disable value-no-vendor-prefix */
            display: -ms-grid;
            display: grid;
            padding: 1.5rem 0 0 1.5rem;
            margin: -1.5rem;
            overflow: hidden;

            @media print {
                display: -ms-grid;
                display: grid;
                overflow: hidden;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            grid-template-columns: minmax(0, 1fr);
                            -ms-grid-columns: minmax(0, 1fr);
                        `,
                        css`
                            @media print {
                                grid-template-columns: minmax(0, 2fr) minmax(0, 0.7fr);
                                -ms-grid-columns: minmax(0, 2fr) minmax(0, 0.7fr);
                            }
                        `,
                        null,
                        css`
                            grid-template-columns: minmax(0, 2fr) minmax(0, 0.7fr);
                            -ms-grid-columns: minmax(0, 2fr) minmax(0, 0.7fr);
                        `,
                    ],
                    "min",
                )}
        `,
    },
    headerContainer: {
        css: css`
            h1 {
                margin-bottom: 1rem;
                @media print {
                    margin-bottom: 10px;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                            @media print {
                                border-bottom: none;
                            }
                        `,
                        null,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-column-end: 3;
                            -ms-grid-column-span: 3;
                            grid-row-start: 1;
                            -ms-grid-row: 1;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartLinesContainer: {
        css: css`
            button,
            input {
                &:disabled {
                    color: #ccc;

                    span:last-child {
                        color: #ccc;
                    }
                    
                    svg {
                        opacity: 0.5;
                    }
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;

                            .side-wrapper {
                                background-color: #fff;

                                .border-seperator {
                                    border-bottom: 1px solid #C2D1D9;
                                    padding-top: 15px;
                                    margin-bottom: 15px;
                                    width: 100%;
                                    display: block;
                                }
                                .grid-container {
                                    padding-left: 0 !important;
                                    padding-right: 0 !important;

                                    > div {
                                        div:nth-child(odd) {
                                            > span {
                                                color: #7C878E;
                                                font-family: Roboto, sans-serif;
                                                font-size: 16px;
                                                font-weight: 500;
                                                letter-spacing: -0.31px;
                                                line-height: 23px;

                                                &.total-value {
                                                    opacity: 0.7;
                                                    color: #C63527;
                                                    font-family: Roboto;
                                                    font-size: 22px;
                                                    font-weight: bold;
                                                    letter-spacing: -0.42px;
                                                    line-height: 23px;
                                                    text-align: right;
                                                }
                                            }
                                            > span[data-test-selector="cartTotal_subTotal"] {
                                                font-weight: bold;
                                            }
                                            .total-label {
                                                color: #333F48;
                                                font-family: Roboto, sans-serif;
                                                font-size: 20px;
                                                font-weight: 500;
                                                letter-spacing: -0.38px;
                                                line-height: 23px;
                                                text-align: left !important;
                                            }
                                        }
                                        div:nth-child(even) {
                                            text-align: right;
                                            > span {
                                                color: #333F48;
                                                font-family: Roboto;
                                                font-size: 20px;
                                                letter-spacing: -0.38px;
                                                line-height: 23px;
                                            }
                                            > span[data-test-selector="cartTotal_subTotal"] {
                                                font-weight: bold;
                                            }
                                        }
                                    }
                                }
                                @media print {
                                    display: none;
                                }
                            }
                            .side-wrapper-top {
                                background-color: #fff;
                                padding-left: 0 !important;
                                padding-right: 0 !important;
                            }
                            .cart-items {
                                display: block !important;
                            }
                        `,
                        null,
                        null,
                        css`
                            grid-column-start: 1;
                            -ms-grid-column: 1;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                            grid-row-end: 6;
                            -ms-grid-row-span: 6;
                            .hide-top-view-desktop {
                                display: none;
                            }
                        `,
                    ],
                    "min",
                )}

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            position: relative;
                            margin-top: 20px;

                            .grid-container {
                                max-width: 100%;
                                padding: 0;
                            }

                            button[data-test-selector="cart_saveOrder"] {
                                position: absolute;
                                top: -30px;
                                left: -14px;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    fulfillmentMethodContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: none;
                        `,
                        null,
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 2;
                            -ms-grid-row: 2;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    cartTotalContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0;
                            margin-right: 1.5rem;
                            padding-top: 15px;
                            
                            .side-wrapper {
                                background-color: #fff;

                                .border-seperator {
                                    border-bottom: 1px solid #C2D1D9;
                                    padding-top: 15px;
                                    margin-bottom: 15px;
                                    width: 100%;
                                    display: block;
                                }
                                .grid-container {
                                    padding-left: 0 !important;
                                    padding-right: 0 !important;

                                    > div {
                                        div:nth-child(odd) {
                                            > span {
                                                color: #7C878E;
                                                font-family: Roboto, sans-serif;
                                                font-size: 16px;
                                                font-weight: 500;
                                                letter-spacing: -0.31px;
                                                line-height: 23px;

                                                &.total-value {
                                                    opacity: 0.7;
                                                    color: #C63527;
                                                    font-family: Roboto;
                                                    font-size: 22px;
                                                    font-weight: bold;
                                                    letter-spacing: -0.42px;
                                                    line-height: 23px;
                                                    text-align: right !important;
                                                    display: block;
                                                }
                                            }
                                            > span[data-test-selector="cartTotal_subTotal"] {
                                                font-weight: bold;
                                            }
                                            .total-label {
                                                color: #333F48;
                                                font-family: Roboto, sans-serif;
                                                font-size: 20px;
                                                font-weight: 500;
                                                letter-spacing: -0.38px;
                                                line-height: 23px;
                                                text-align: left !important;
                                            }
                                        }
                                        div:nth-child(even) {
                                            text-align: right;
                                            > span {
                                                color: #333F48;
                                                font-family: Roboto;
                                                font-size: 20px;
                                                letter-spacing: -0.38px;
                                                line-height: 23px;

                                                &.total-label {
                                                    color: #333F48;
                                                    text-align: left !important;
                                                    display: block;
                                                }
                                            }
                                            > span[data-test-selector="cartTotal_subTotal"] {
                                                font-weight: bold;
                                            }
                                        }
                                    }
                                }
                            }
                            .side-wrapper-top {
                                background-color: #fff;
                                padding-left: 0 !important;
                                padding-right: 0 !important;
                            }
                            .cart-items {
                                display: block !important;
                            }
                        `,
                        null,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 3;
                            -ms-grid-row: 3;
                        `,
                    ],
                    "min",
                )}

                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            .grid-container {
                                max-width: 100%;
                                padding: 0;
                            }

                            button[data-test-selector="cart_saveOrder"] {
                                display: none;
                            }
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    productCarouselContainer: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            border: 0 solid transparent;
                            border-width: 0 1.5rem 1.5rem 0;
                        `,
                        null,
                        null,
                        css`
                            grid-column-start: 2;
                            -ms-grid-column: 2;
                            grid-row-start: 4;
                            -ms-grid-row: 4;
                        `,
                    ],
                    "min",
                )}
        `,
    },
};

const styles = cartPageContainerStyles;

const CartPageContainer = ({ id }: WidgetProps) => {
    let featureFlags: string[] = ["TEUS-8247"];
    const flagValues = useLaunchDarklyFlags({ featureFlags });
    const enableMobileCartLineEasierDesign = flagValues["TEUS-8247"];

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.headerContainer} className="cart-header-container">
                <Zone zoneName="Content0" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.cartLinesContainer} className="cart-lines-container" style={{
                marginTop: enableMobileCartLineEasierDesign ? '0px' : undefined,
            }}>
                <Zone zoneName="Content2" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.fulfillmentMethodContainer} className="cart-method-container">
                <Zone zoneName="Content3" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.cartTotalContainer} className="cart-total-container">
                <Zone zoneName="Content1" contentId={id} />
            </StyledWrapper>
            <StyledWrapper {...styles.productCarouselContainer}>
                <Zone zoneName="Content4" contentId={id} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: CartPageContainer,
    definition: {
        group: "Cart",
        allowedContexts: [CartPageContext],
        displayName: "Page Container",
    },
};

export default widgetModule;
